import { useEffect, useRef } from 'react';
import { Avatar, Box, Button, Dialog, IconButton, Stack, Tooltip, Typography, alpha, useMediaQuery, useTheme } from '@mui/material';
import CallEndRounded from '@mui/icons-material/CallEndRounded';
import MicRounded from '@mui/icons-material/MicRounded';
import MicOffRounded from '@mui/icons-material/MicOffRounded';
import VideocamRounded from '@mui/icons-material/VideocamRounded';
import VideocamOffRounded from '@mui/icons-material/VideocamOffRounded';
import PresentToAllRounded from '@mui/icons-material/PresentToAllRounded';
import CancelPresentationRounded from '@mui/icons-material/CancelPresentationRounded';
import CloseFullscreenRounded from '@mui/icons-material/CloseFullscreenRounded';
import VolumeUpRounded from '@mui/icons-material/VolumeUpRounded';
import { Track, type Participant } from 'livekit-client';
import { ORO, VERDE } from '../enVivo/estilo';
import type { useSalaDeLlamada } from './useSalaDeLlamada';

type Sala = ReturnType<typeof useSalaDeLlamada>;
const FONDO = '#0b0f14';
const ROJO = '#dc2626';

/** El video de un participante: su pantalla compartida si la hay, si no la cámara. */
function videoDe(p: Participant) {
  const pantalla = p.getTrackPublication(Track.Source.ScreenShare);
  if (pantalla?.track && !pantalla.isMuted) return { track: pantalla.track, esPantalla: true };
  const cam = p.getTrackPublication(Track.Source.Camera);
  if (cam?.track && !cam.isMuted) return { track: cam.track, esPantalla: false };
  return null;
}

function Mosaico({ p, hablando, esLocal }: { p: Participant; hablando: boolean; esLocal: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  const video = videoDe(p);
  const track = video?.track;
  useEffect(() => {
    const el = ref.current;
    if (!track || !el) return;
    track.attach(el);
    return () => { track.detach(el); };
  }, [track]);
  const nombre = p.name || (esLocal ? 'Vos' : 'Invitado');
  const micApagado = !p.isMicrophoneEnabled;

  return (
    <Box sx={{
      position: 'relative', borderRadius: 3, overflow: 'hidden', bgcolor: '#151a22', minHeight: 0,
      outline: `2px solid ${hablando ? VERDE : 'transparent'}`, transition: 'outline-color .2s', display: 'grid', placeItems: 'center',
    }}>
      {track ? (
        <video ref={ref} autoPlay playsInline muted={esLocal}
          style={{ width: '100%', height: '100%', objectFit: video?.esPantalla ? 'contain' : 'cover', transform: esLocal && !video?.esPantalla ? 'scaleX(-1)' : undefined }} />
      ) : (
        <Avatar sx={{ width: 72, height: 72, fontSize: 28, fontWeight: 800, bgcolor: alpha(ORO, 0.18), color: '#fff', border: `2px solid ${alpha(hablando ? VERDE : ORO, 0.5)}` }}>
          {nombre.slice(0, 1).toUpperCase()}
        </Avatar>
      )}
      <Stack direction="row" alignItems="center" spacing={0.5}
        sx={{ position: 'absolute', left: 8, bottom: 8, px: 1, py: 0.25, borderRadius: 999, bgcolor: alpha('#000', 0.55), maxWidth: 'calc(100% - 16px)' }}>
        {micApagado && <MicOffRounded sx={{ fontSize: 14, color: '#f87171' }} />}
        <Typography noWrap sx={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>{esLocal ? `${nombre} (vos)` : nombre}</Typography>
      </Stack>
    </Box>
  );
}

function Control({ titulo, activo, onClick, children, peligro }: { titulo: string; activo?: boolean; onClick: () => void; children: React.ReactNode; peligro?: boolean }) {
  return (
    <Tooltip title={titulo}>
      <IconButton aria-label={titulo} aria-pressed={activo} onClick={onClick}
        sx={{
          width: 52, height: 52, color: '#fff',
          bgcolor: peligro ? ROJO : activo === false ? alpha('#fff', 0.9) : alpha('#fff', 0.12),
          ...(activo === false ? { color: FONDO } : {}),
          '&:hover': { bgcolor: peligro ? '#b91c1c' : activo === false ? '#fff' : alpha('#fff', 0.2) },
          '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 },
        }}>
        {children}
      </IconButton>
    </Tooltip>
  );
}

/**
 * LA PANTALLA DE LA LLAMADA (fase 3, 14/10/2026). Se abre desde la barra de la llamada; en el
 * celular ocupa toda la pantalla. Minimizarla NO corta: la sala vive en LlamadaDelChat.
 */
export function PantallaDeLlamada({ abierta, onMinimizar, onCortar, titulo, detalle, sala }: {
  abierta: boolean; onMinimizar: () => void; onCortar: () => void; titulo: string; detalle: string; sala: Sala;
}) {
  const theme = useTheme();
  const celular = useMediaQuery(theme.breakpoints.down('sm'));
  const puedeCompartir = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia && !celular;
  const ps = sala.participantes;
  // En el celular parado, dos personas van una arriba de la otra (lado a lado quedan finitas).
  const columnas = ps.length <= 1 || (celular && ps.length === 2) ? 1 : ps.length <= 4 || celular ? 2 : 3;
  const filas = Math.ceil(Math.max(1, ps.length) / columnas);
  const aviso = sala.estado === 'conectando' ? 'Conectando…'
    : sala.estado === 'reconectando' ? 'Reconectando…'
    : sala.estado === 'sin-configurar' ? 'El audio de las llamadas todavía no está configurado'
    : sala.estado === 'error' ? 'No se pudo conectar el audio. Cortá y volvé a llamar.' : '';

  return (
    <Dialog open={abierta} onClose={onMinimizar} fullScreen={celular} maxWidth="lg" fullWidth
      slotProps={{ paper: { sx: { bgcolor: FONDO, color: '#fff', backgroundImage: 'none', height: celular ? '100%' : 'min(86vh, 760px)', borderRadius: celular ? 0 : 4, border: celular ? 'none' : `1px solid ${alpha('#fff', 0.08)}` } } }}>
      <Stack sx={{ height: '100%', p: { xs: 1.5, sm: 2 }, pt: { xs: 'max(12px, env(safe-area-inset-top))', sm: 2 }, pb: { xs: 'max(12px, env(safe-area-inset-bottom))', sm: 2 }, gap: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box flex={1} minWidth={0}>
            <Typography noWrap sx={{ fontWeight: 800, fontSize: 16 }}>{titulo}</Typography>
            <Typography noWrap sx={{ fontSize: 12.5, color: aviso ? ORO : alpha('#fff', 0.65) }}>{aviso || detalle}</Typography>
          </Box>
          <Tooltip title="Minimizar">
            <IconButton aria-label="Minimizar" onClick={onMinimizar} sx={{ color: '#fff' }}><CloseFullscreenRounded /></IconButton>
          </Tooltip>
        </Stack>

        {sala.audioBloqueado && (
          <Button onClick={sala.activarAudio} startIcon={<VolumeUpRounded />} variant="contained"
            sx={{ alignSelf: 'center', borderRadius: 999, textTransform: 'none', fontWeight: 700, bgcolor: ORO, color: FONDO, '&:hover': { bgcolor: ORO } }}>
            Tocá para escuchar
          </Button>
        )}

        <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gap: 1, gridTemplateColumns: `repeat(${columnas}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${filas}, minmax(0, 1fr))` }}>
          {ps.map((p, i) => <Mosaico key={p.identity} p={p} esLocal={i === 0} hablando={sala.hablando.has(p.identity)} />)}
        </Box>

        <Stack direction="row" justifyContent="center" alignItems="center" spacing={{ xs: 1.5, sm: 2 }}>
          <Control titulo={sala.micPrendido ? 'Silenciar' : 'Activar micrófono'} activo={sala.micPrendido} onClick={() => sala.alternar('mic')}>
            {sala.micPrendido ? <MicRounded /> : <MicOffRounded />}
          </Control>
          <Control titulo={sala.camPrendida ? 'Apagar cámara' : 'Prender cámara'} activo={sala.camPrendida ? true : undefined} onClick={() => sala.alternar('cam')}>
            {sala.camPrendida ? <VideocamRounded /> : <VideocamOffRounded />}
          </Control>
          {puedeCompartir && (
            <Control titulo={sala.pantallaCompartida ? 'Dejar de compartir' : 'Compartir pantalla'} activo={sala.pantallaCompartida ? true : undefined} onClick={() => sala.alternar('pantalla')}>
              {sala.pantallaCompartida ? <CancelPresentationRounded /> : <PresentToAllRounded />}
            </Control>
          )}
          <Control titulo="Cortar" peligro onClick={onCortar}><CallEndRounded /></Control>
        </Stack>
      </Stack>
    </Dialog>
  );
}
